import * as React from 'react'

import { cn } from '@/lib/utils'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { X } from 'lucide-react'
import { Icon } from '@/common/components/Icon'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'auto'

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-[calc(100%-32px)] md:max-w-[460px]',
  md: 'max-w-[calc(100%-32px)] md:max-w-2xl',
  lg: 'max-w-[calc(100%-32px)] md:max-w-4xl',
  xl: 'max-w-[calc(100%-32px)] md:max-w-6xl',
  auto: 'max-w-auto md:max-w-auto w-auto',
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  contentClassName,
  headerClassName,
  tabIndex,
  wrapperClassName,
  footerClassName,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  size?: ModalSize
  contentClassName?: string
  headerClassName?: string
  tabIndex?: number
  wrapperClassName?: string
  footerClassName?: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          sizeClass[size],
          // keep close button visible outside the frame
          'overflow-visible !rounded-3xl bg-gradient-4 border p-0 border-monochrome-120 [&>button:last-child]:hidden',
          contentClassName,
        )}
        tabIndex={tabIndex}
      >
        <DialogClose className="absolute text-monochrome-1 cursor-pointer -right-8 -top-8 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent-5 data-[state=open]:text-monochrome-1 bg-monochrome-5-50 border w-[40px] h-[40px] flex items-center justify-center border-monochrome-120 rounded-full">
          <Icon
            icon="close"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          />
          <span className="sr-only">Close</span>
        </DialogClose>
        <div
          className={cn(
            'max-h-[80svh] overflow-y-auto p-[30px]',
            wrapperClassName,
          )}
        >
          <DialogHeader className={cn('mb-[30px]', headerClassName)}>
            <DialogTitle asChild className="mb-0">
              {title}
            </DialogTitle>

            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          {children}

          {footer && (
            <DialogFooter
              className={cn(
                'flex flex-row items-center gap-2 [&_button]:min-w-[154px] [&_button]:h-[44px] mt-[25px]',
                footerClassName,
              )}
            >
              {footer}
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
